import React, { useState, useEffect } from "react";
import { toast } from "react-toastify";
import { campusApi } from "../campus/campus-api";
import { Campus, Node, Camera } from "../campus/types";
import CameraPlayer from "./CameraPlayer";
import HlsPlayer from "./HLSPlayer";

interface ViewingCamera {
    id: string;
    camera: Camera;
    node: Node;
    campus: Campus;
    streamUrl?: string;
    isMaximized: boolean;
    size: { width: number; height: number };
    position: { x: number; y: number };
}

const VideoViewer: React.FC = () => {
    const [campuses, setCampuses] = useState<Campus[]>([]);
    const [nodes, setNodes] = useState<Node[]>([]);
    const [cameras, setCameras] = useState<Camera[]>([]);
    const [viewingCameras, setViewingCameras] = useState<ViewingCamera[]>([]);

    const [selectedCampusId, setSelectedCampusId] = useState<number | null>(null);
    const [selectedNodeId, setSelectedNodeId] = useState<number | null>(null);
    const [selectedCameraId, setSelectedCameraId] = useState<number | null>(null);

    const [loadingCampuses, setLoadingCampuses] = useState(false);
    const [loadingNodes, setLoadingNodes] = useState(false);
    const [loadingCameras, setLoadingCameras] = useState(false);
    const [loadingStream, setLoadingStream] = useState<number | null>(null);

    const mmtxHost = "localhost";
    const mmtxPort = 8889;
    const hlsPort = 8888;

    // Fetch campuses on mount
    useEffect(() => {
        fetchCampuses();
    }, []);

    // Fetch nodes when campus is selected
    useEffect(() => {
        if (selectedCampusId) {
            fetchNodes(selectedCampusId);
            setSelectedNodeId(null);
            setSelectedCameraId(null);
            setCameras([]);
        }
    }, [selectedCampusId]);

    // Fetch cameras when node is selected
    useEffect(() => {
        if (selectedNodeId) {
            fetchCameras(selectedNodeId);
            setSelectedCameraId(null);
        }
    }, [selectedNodeId]);

    const fetchCampuses = async () => {
        setLoadingCampuses(true);
        try {
            const data = await campusApi.getAllCampuses();            
            setCampuses(data);
        } catch (error: any) {
            toast.error("Failed to load campuses");
        } finally {
            setLoadingCampuses(false);
        }
    };

    const fetchNodes = async (campusId: number) => {
        setLoadingNodes(true);
        try {
            const data = await campusApi.getNodesByCampus(campusId);
            setNodes(data);
        } catch (error: any) {
            toast.error("Failed to load nodes");
            setNodes([]);
        } finally {
            setLoadingNodes(false);
        }
    };

    const fetchCameras = async (nodeId: number) => {
        setLoadingCameras(true);
        try {
            const data = await campusApi.getCamerasByNode(nodeId);
            setCameras(data);
        } catch (error: any) {
            toast.error("Failed to load cameras");
            setCameras([]);
        } finally {
            setLoadingCameras(false);
        }
    };

    const addCameraToView = async () => {
        if (!selectedCampusId || !selectedNodeId || !selectedCameraId) {
            toast.error("Please select campus, node, and camera");
            return;
        }

        const camera = cameras.find((c) => c.id === selectedCameraId);
        const node = nodes.find((n) => n.id === selectedNodeId);
        const campus = campuses.find((c) => c.id === selectedCampusId);

        if (!camera || !node || !campus) {
            toast.error("Invalid selection");
            return;
        }

        // Check if camera is already in viewing list
        if (viewingCameras.some((vc) => vc.camera.id === camera.id)) {
            toast.info("Camera is already in the viewing list");
            return;
        }

        setLoadingStream(camera.id);
        try {
            // Try to get stream URL from API
            let streamUrl: string | undefined;
            try {
                streamUrl = await campusApi.getCameraLiveFeed(camera.id);
            } catch (err) {
                console.warn("Failed to get stream URL from API, using default path");
            }

            // Generate camera path (you may need to adjust this based on your MediaMTX setup)
            //   const cameraPath = streamUrl || `cam_${camera.id}`;
            const cameraPath = `http://localhost:8888/${camera.cam_id}/index.m3u8`;
            const viewingCamera: ViewingCamera = {
                id: `camera-${camera.id}-${Date.now()}`,
                camera,
                node,
                campus,
                streamUrl: cameraPath,
                isMaximized: false,
                size: { width: 400, height: 300 },
                position: { x: 0, y: 0 },
            };

            setViewingCameras([...viewingCameras, viewingCamera]);
            toast.success("Camera added to viewing list");
        } catch (error: any) {
            toast.error("Failed to add camera");
        } finally {
            setLoadingStream(null);
        }
    };

    const removeCamera = (id: string) => {
        setViewingCameras(viewingCameras.filter((vc) => vc.id !== id));
    };

    const toggleMaximize = (id: string) => {
        setViewingCameras(
            viewingCameras.map((vc) =>
                vc.id === id ? { ...vc, isMaximized: !vc.isMaximized } : vc
            )
        );
    };

    const updateCameraSize = (id: string, width: number, height: number) => {
        setViewingCameras(
            viewingCameras.map((vc) =>
                vc.id === id ? { ...vc, size: { width, height } } : vc
            )
        );
    };

    const selectedCampus = campuses.find((c) => c.id === selectedCampusId);
    const selectedNode = nodes.find((n) => n.id === selectedNodeId);
    const selectedCamera = cameras.find((c) => c.id === selectedCameraId);

    return (
        <div className="min-h-screen bg-gray-50 dark:bg-gray-950 py-8 px-4">
            <div className="max-w-[1800px] mx-auto">
                <h1 className="text-3xl font-bold text-gray-900 dark:text-white mb-6">
                    Camera Viewer
                </h1>

                {/* Selection Panel */}
                <div className="bg-white dark:bg-gray-800 rounded-xl shadow-lg border border-gray-200 dark:border-gray-700 p-6 mb-6">
                    <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-4">
                        Select Camera
                    </h2>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        {/* Campus Selection */}
                        <div>
                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                Campus
                            </label>
                            <select
                                value={selectedCampusId || ""}
                                onChange={(e) => setSelectedCampusId(Number(e.target.value) || null)}
                                disabled={loadingCampuses}
                                className="w-full px-4 py-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                            >
                                <option value="">Select Campus</option>
                                {campuses.map((campus) => (
                                    <option key={campus.id} value={campus.id}>
                                        {campus.name} - {campus.location}
                                    </option>
                                ))}
                            </select>
                        </div>

                        {/* Node Selection */}
                        <div>
                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                Node
                            </label>
                            <select
                                value={selectedNodeId || ""}
                                onChange={(e) => setSelectedNodeId(Number(e.target.value) || null)}
                                disabled={!selectedCampusId || loadingNodes}
                                className="w-full px-4 py-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:opacity-50"
                            >
                                <option value="">Select Node</option>
                                {nodes.map((node) => (
                                    <option key={node.node_id} value={node.id}>
                                        {node.node_name}
                                    </option>
                                ))}
                            </select>
                        </div>

                        {/* Camera Selection */}
                        <div>
                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                Camera
                            </label>
                            <select
                                value={selectedCameraId || ""}
                                onChange={(e) => setSelectedCameraId(Number(e.target.value) || null)}
                                disabled={!selectedNodeId || loadingCameras}
                                className="w-full px-4 py-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:opacity-50"
                            >
                                <option value="">Select Camera</option>
                                {cameras.map((camera) => (
                                    <option key={camera.id} value={camera.id}>
                                        Camera {camera.id} ({camera.type})
                                    </option>
                                ))}
                            </select>
                        </div>
                    </div>

                    <button
                        onClick={addCameraToView}
                        disabled={!selectedCameraId || loadingStream !== null}
                        className="mt-4 btn-primary px-6 py-2 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                        {loadingStream ? "Adding..." : "Add to Viewing List"}
                    </button>
                </div>

                {/* Viewing List */}
                {viewingCameras.length > 0 && (
                    <div className="bg-white dark:bg-gray-800 rounded-xl shadow-lg border border-gray-200 dark:border-gray-700 p-6">
                        <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-4">
                            Active Cameras ({viewingCameras.length})
                        </h2>
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
                            {viewingCameras.map((vc) => (
                                <CameraPreviewBox
                                    key={vc.id}
                                    viewingCamera={vc}
                                    onRemove={removeCamera}
                                    onToggleMaximize={toggleMaximize}
                                    onUpdateSize={updateCameraSize}
                                    mmtxHost={mmtxHost}
                                    mmtxPort={mmtxPort}
                                    hlsPort={hlsPort}
                                />
                            ))}
                        </div>
                    </div>
                )}

                {/* Empty State */}
                {viewingCameras.length === 0 && (
                    <div className="bg-white dark:bg-gray-800 rounded-xl shadow-lg border border-gray-200 dark:border-gray-700 p-12 text-center">
                        <p className="text-gray-500 dark:text-gray-400">
                            No cameras in viewing list. Select a camera above to get started.
                        </p>
                    </div>
                )}
            </div>
        </div>
    );
};

interface CameraPreviewBoxProps {
    viewingCamera: ViewingCamera;
    onRemove: (id: string) => void;
    onToggleMaximize: (id: string) => void;
    onUpdateSize: (id: string, width: number, height: number) => void;
    mmtxHost: string;
    mmtxPort: number;
    hlsPort: number;
}

const CameraPreviewBox: React.FC<CameraPreviewBoxProps> = ({
    viewingCamera,
    onRemove,
    onToggleMaximize,
    onUpdateSize,
    mmtxHost,
    mmtxPort,
    hlsPort,
}) => {
    const [isResizing, setIsResizing] = useState(false);
    const [resizeStart, setResizeStart] = useState({ x: 0, y: 0, width: 0, height: 0 });
    const boxRef = React.useRef<HTMLDivElement>(null);

    const handleMouseDown = (e: React.MouseEvent) => {
        if (viewingCamera.isMaximized) return;
        e.preventDefault();
        setIsResizing(true);
        setResizeStart({
            x: e.clientX,
            y: e.clientY,
            width: viewingCamera.size.width,
            height: viewingCamera.size.height,
        });
    };

    useEffect(() => {
        const handleMouseMove = (e: MouseEvent) => {
            if (!isResizing) return;
            const deltaX = e.clientX - resizeStart.x;
            const deltaY = e.clientY - resizeStart.y;
            const newWidth = Math.max(200, resizeStart.width + deltaX);
            const newHeight = Math.max(150, resizeStart.height + deltaY);
            onUpdateSize(viewingCamera.id, newWidth, newHeight);
        };

        const handleMouseUp = () => {
            setIsResizing(false);
        };

        if (isResizing) {
            document.addEventListener("mousemove", handleMouseMove);
            document.addEventListener("mouseup", handleMouseUp);
        }

        return () => {
            document.removeEventListener("mousemove", handleMouseMove);
            document.removeEventListener("mouseup", handleMouseUp);
        };
    }, [isResizing, resizeStart, viewingCamera.id, onUpdateSize]);

    // Generate camera path/URL
    // const cameraPath = viewingCamera.streamUrl || `cam_${viewingCamera.camera.id}`;
    // const hlsUrl = viewingCamera.streamUrl
    // ? viewingCamera.streamUrl
    // : `http://${mmtxHost}:${hlsPort}/${cameraPath}/index.m3u8`;
    // const hlsUrl = "http://localhost:8888/cam9/index.m3u8";

    if (viewingCamera.isMaximized) {
        return (
            <div className="fixed inset-0 z-50 bg-black flex items-center justify-center">
                <div className="relative w-full h-full flex flex-col">
                    <div className="absolute top-4 right-4 z-10 flex gap-2">
                        <button
                            onClick={() => onToggleMaximize(viewingCamera.id)}
                            className="bg-gray-800 hover:bg-gray-700 text-white px-4 py-2 rounded-lg flex items-center gap-2"
                        >
                            <i className="fa-solid fa-compress"></i>
                            Minimize
                        </button>
                        <button
                            onClick={() => onRemove(viewingCamera.id)}
                            className="bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded-lg flex items-center gap-2"
                        >
                            <i className="fa-solid fa-times"></i>
                            Close
                        </button>
                    </div>
                    <div className="w-full h-full relative">
                        {/* {viewingCamera.streamUrl?.includes(".m3u8") || hlsUrl.includes(".m3u8") ? ( */}
                        <HlsPlayer src={viewingCamera.streamUrl!} autoPlay muted />
                        {/* ) : (
                            <CameraPlayer
                                path={cameraPath}
                                host={mmtxHost}
                                port={mmtxPort}
                                className="w-full h-full"
                                showStatus={false}
                            />
                        )} */}
                    </div>
                    <div className="absolute bottom-4 left-4 bg-black/70 text-white px-4 py-2 rounded-lg">
                        <div className="text-sm font-medium">
                            {viewingCamera.campus.name} - {viewingCamera.node.node_name}
                        </div>
                        <div className="text-xs text-gray-300">
                            Camera {viewingCamera.camera.id} ({viewingCamera.camera.type})
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div
            ref={boxRef}
            className="relative bg-gray-900 dark:bg-gray-900 rounded-lg overflow-hidden shadow-lg border border-gray-700"
            style={{
                width: `${viewingCamera.size.width}px`,
                height: `${viewingCamera.size.height}px`,
            }}
        >
            {/* Video Player */}
            <div className="w-full h-full relative">
                {/* {viewingCamera.streamUrl?.includes(".m3u8") || hlsUrl.includes(".m3u8") ? ( */}
                <HlsPlayer src={viewingCamera.streamUrl!} autoPlay muted />
                {/* ) : (
                    <CameraPlayer
                        path={cameraPath}
                        host={mmtxHost}
                        port={mmtxPort}
                        className="w-full h-full"
                        showStatus={false}
                    />
                )} */}
            </div>

            {/* Controls Overlay */}
            <div className="absolute top-2 right-2 flex gap-2 opacity-0 hover:opacity-100 transition-opacity">
                <button
                    onClick={() => onToggleMaximize(viewingCamera.id)}
                    className="bg-gray-800/80 hover:bg-gray-700/80 text-white p-2 rounded"
                    title="Maximize"
                >
                    <i className="fa-solid fa-expand text-xs"></i>
                </button>
                <button
                    onClick={() => onRemove(viewingCamera.id)}
                    className="bg-red-600/80 hover:bg-red-700/80 text-white p-2 rounded"
                    title="Remove"
                >
                    <i className="fa-solid fa-times text-xs"></i>
                </button>
            </div>

            {/* Info Overlay */}
            <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/80 to-transparent p-2">
                <div className="text-white text-xs">
                    <div className="font-medium truncate">
                        {viewingCamera.campus.name} - {viewingCamera.node.node_name}
                    </div>
                    <div className="text-gray-300 truncate">
                        Camera {viewingCamera.camera.id} ({viewingCamera.camera.type})
                    </div>
                </div>
            </div>

            {/* Resize Handle */}
            <div
                onMouseDown={handleMouseDown}
                className="absolute bottom-0 right-0 w-4 h-4 cursor-nwse-resize bg-gray-600/50 hover:bg-gray-500/50"
                style={{
                    clipPath: "polygon(100% 0, 0 100%, 100% 100%)",
                }}
            />
        </div>
    );
};

export default VideoViewer;
